import { Routes, Route } from 'react-router-dom'
import { Toaster } from '@components/ui/toaster'

import { DashboardLayout } from '@components/layout/DashboardLayout'
import { Dashboard } from '@pages/Dashboard'
import { PatientsPage } from '@pages/patients/PatientsPage'
import { PatientDetailPage } from '@pages/patients/PatientDetailPage'
import { AppointmentsPage } from '@pages/appointments/AppointmentsPage'
import { AgendaPage } from '@pages/agenda/AgendaPage'
import { BillingPage } from '@pages/billing/BillingPage'
import { PatientBillingHistoryPage } from '@pages/billing/PatientBillingHistoryPage'
import { SafetyInboxPage } from '@pages/safety/SafetyInboxPage'
import { SettingsPage } from '@pages/settings/SettingsPage'
import { ClinicManagementPage } from '@pages/settings/ClinicManagementPage'
import { LoginPage } from '@pages/auth/LoginPage'
import { ForgotCredentialsPage } from '@pages/auth/ForgotCredentialsPage'
import { DepartmentAnalyticsPage } from '@pages/analytics'
import { AuditLogsPage } from '@pages/audit/AuditLogsPage'
import { OrganizationsPage } from '@pages/organizations/OrganizationsPage'
import { FeatureFlagsPage } from '@pages/feature-flags/FeatureFlagsPage'
import { AIChatPage } from '@pages/ai-chat/AIChatPage'
import { MyAvailabilityPage } from '@pages/availability/MyAvailabilityPage'
import { LandingPage } from '@pages/landing/LandingPage'
import { ClinicSelectorPage } from '@pages/ClinicSelectorPage'

import { PortalProtectedRoute } from '@components/auth/PortalProtectedRoute'
import {
  PortalLoginPage,
  PortalRegisterPage,
  PortalLayout,
  PortalDashboardPage,
  PortalAppointmentsPage,
  PortalPrescriptionsPage,
  PortalRecordsPage,
  PortalMessagesPage,
  PortalBillingPage,
  PortalProfilePage,
} from '@pages/portal'

import { ProtectedRoute } from '@components/auth/ProtectedRoute'
import { PermissionGuard } from '@hooks/usePermissions'
import { ActivePatientProvider } from '@context/ActivePatientContext'
import { ClinicTimezoneProvider } from '@context/ClinicTimezoneContext'
import {
  PatientWorkspace,
  ResumenDiagnosticoTab,
  HistorialCitasTab,
  NotasRecepcionTab,
  DatosPacienteTab,
  HistoriaClinicaTab,
  ProgressNotesTab,
} from '@pages/workspace'

function AccessDeniedMessage() {
  return (
    <div className="flex items-center justify-center h-full p-8">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-ml-on-surface mb-2">Access Denied</h2>
        <p className="text-ml-on-surface-variant">You do not have permission to access this section.</p>
      </div>
    </div>
  )
}

function App() {
  return (
    <ActivePatientProvider>
      <ClinicTimezoneProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-credentials" element={<ForgotCredentialsPage />} />

        {/* Portal routes - Public */}
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/register" element={<PortalRegisterPage />} />

        {/* Portal routes - Protected */}
        <Route element={<PortalProtectedRoute />}>
          <Route element={<PortalLayout />}>
            <Route path="/portal/dashboard" element={<PortalDashboardPage />} />
            <Route path="/portal/appointments" element={<PortalAppointmentsPage />} />
            <Route path="/portal/prescriptions" element={<PortalPrescriptionsPage />} />
            <Route path="/portal/records" element={<PortalRecordsPage />} />
            <Route path="/portal/messages" element={<PortalMessagesPage />} />
            <Route path="/portal/billing" element={<PortalBillingPage />} />
            <Route path="/portal/profile" element={<PortalProfilePage />} />
          </Route>
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>

          {/* Clinic selection for multi-clinic users */}
          <Route path="/select-clinic" element={<ClinicSelectorPage />} />

          <Route path="/workspace/patient/:patientId" element={<PatientWorkspace />}>
            <Route path="resumen" element={<ResumenDiagnosticoTab />} />
            <Route path="diagnostico" element={<ResumenDiagnosticoTab />} />
            <Route path="historial-citas" element={<HistorialCitasTab />} />
            <Route path="notas" element={<NotasRecepcionTab />} />
            <Route path="datos-paciente" element={<DatosPacienteTab />} />
            <Route path="historia-clinica" element={<HistoriaClinicaTab />} />
            <Route path="consulta" element={<ProgressNotesTab />} />
          </Route>

          {/* Main Dashboard Layout Routes */}
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/patients" element={
              <PermissionGuard section="patient_records" fallback={<AccessDeniedMessage />}>
                <PatientsPage />
              </PermissionGuard>
            } />
            <Route path="/patients/:id" element={
              <PermissionGuard section="patient_records" fallback={<AccessDeniedMessage />}>
                <PatientDetailPage />
              </PermissionGuard>
            } />
            <Route path="/agenda" element={
              <PermissionGuard section="appointments" fallback={<AccessDeniedMessage />}>
                <AgendaPage />
              </PermissionGuard>
            } />
            <Route path="/appointments" element={
              <PermissionGuard section="appointments" fallback={<AccessDeniedMessage />}>
                <AppointmentsPage />
              </PermissionGuard>
            } />
            <Route path="/billing" element={
              <PermissionGuard section="billing" fallback={<AccessDeniedMessage />}>
                <BillingPage />
              </PermissionGuard>
            } />
            <Route path="/billing/patients/:patientId" element={
              <PermissionGuard section="billing" fallback={<AccessDeniedMessage />}>
                <PatientBillingHistoryPage />
              </PermissionGuard>
            } />
            <Route path="/safety" element={<SafetyInboxPage />} />
            <Route path="/analytics" element={<DepartmentAnalyticsPage />} />
            <Route path="/audit" element={<AuditLogsPage />} />
            <Route path="/organizations" element={<OrganizationsPage />} />
            <Route path="/feature-flags" element={<FeatureFlagsPage />} />
            <Route path="/ai-chat" element={<AIChatPage />} />
            <Route path="/my-availability" element={<MyAvailabilityPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/clinic" element={<ClinicManagementPage />} />
          </Route>
        </Route>
      </Routes>
      <Toaster />
      </ClinicTimezoneProvider>
    </ActivePatientProvider>
  )
}

export default App

